import baseApi from './baseApi';

interface ZellerAdsResponse {
  success?: boolean;
  error?: string;
  data?: unknown;
  authUrl?: string;
  status?: number;
}

interface AuthUrlResponse {
  success: boolean;
  authUrl: string;
}

interface StatusResponse {
  connected: boolean;
  user?: string;
  accounts?: number;
  message?: string;
}

interface PendingAdAccount {
  id: string;
  name: string;
  accountStatus: number;
  currency: string;
}

interface PendingAdAccountsResponse {
  pendingAdAccounts: PendingAdAccount[];
  connectionId: number;
}

interface BusinessUnit {
  id: number;
  name: string;
  type: string;
}

interface BusinessUnitsResponse {
  businessUnits: BusinessUnit[];
}

interface AssignAdAccountsRequest {
  assignments: Array<{
    adAccountId: string;
    businessUnitId: number;
  }>;
}

interface AssignAdAccountsResponse {
  success: boolean;
  assignedAdAccounts: number;
  remainingPendingAdAccounts: number;
  message: string;
}

interface MetaSyncResponse {
  success: boolean;
  message: string;
  stats: unknown;
  timestamp: string;
}

interface MetaAd {
  id: string;
  name: string;
  status: string;
  adset_id: string;
  campaign_id: string;
  creative?: object;
  tracking_specs?: object;
  conversion_specs?: object;
  lastSyncAt?: string;
}

interface MetaAdSet {
  id: string;
  name: string;
  status: string;
  campaign_id: string;
  daily_budget?: string;
  lifetime_budget?: string;
  start_time?: string;
  end_time?: string;
  targeting?: object;
  optimization_goal?: string;
  billing_event?: string;
  bid_amount?: string;
  lastSyncAt?: string;
  ads: MetaAd[];
}

interface MetaCampaign {
  id: string;
  name: string;
  status: string;
  objective?: string;
  daily_budget?: string;
  lifetime_budget?: string;
  start_time?: string;
  stop_time?: string;
  created_time?: string;
  updated_time?: string;
  buying_type?: string;
  bid_strategy?: string;
  lastSyncAt?: string;
  adSets: MetaAdSet[];
}

interface MetaAdAccount {
  id: string;
  name: string;
  accountStatus: number;
  currency: string;
  lastSyncAt?: string;
  campaigns: MetaCampaign[];
}

interface MetaHierarchyResponse {
  adAccounts: MetaAdAccount[];
  businessUnitId: number;
  count: number;
  stats: {
    adAccounts: number;
    campaigns: number;
    adSets: number;
    ads: number;
  };
  retrievedAt: string;
}

const zellerAdsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getZellerAdsStatus: build.query<StatusResponse, void>({
      query: () => ({
        url: '/zeller-ads/meta/status',
        method: 'GET',
      }),
      transformResponse: (response: StatusResponse) => response,
    }),
    getZellerAdsDebugConfig: build.query<unknown, void>({
      query: () => ({
        url: '/zeller-ads/meta/debug',
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsAuthUrl: build.query<AuthUrlResponse, void>({
      query: () => ({
        url: '/zeller-ads/meta/auth-url',
        method: 'GET',
      }),
      transformResponse: (response: AuthUrlResponse) => response,
    }),
    getZellerAdsAdAccountsSync: build.query<unknown, void>({
      query: () => ({
        url: '/zeller-ads/meta/ad-accounts-sync',
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsAdAccounts: build.query<unknown, void>({
      query: () => ({
        url: '/zeller-ads/meta/ad-accounts',
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsSpecificAdAccount: build.query<unknown, string>({
      query: (adAccountId: string) => ({
        url: `/zeller-ads/meta/ad-account/${adAccountId}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsCampaigns: build.query<unknown, { adAccountId: string; limit?: string }>({
      query: ({ adAccountId, limit }) => ({
        url: `/zeller-ads/meta/ad-account/${adAccountId}/campaigns${limit ? `?limit=${limit}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsAdSets: build.query<unknown, { adAccountId: string; limit?: string }>({
      query: ({ adAccountId, limit }) => ({
        url: `/zeller-ads/meta/ad-account/${adAccountId}/adsets${limit ? `?limit=${limit}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsAds: build.query<unknown, { adAccountId: string; limit?: string }>({
      query: ({ adAccountId, limit }) => ({
        url: `/zeller-ads/meta/ad-account/${adAccountId}/ads${limit ? `?limit=${limit}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsAdSetsFromCampaign: build.query<unknown, { campaignId: string; limit?: string }>({
      query: ({ campaignId, limit }) => ({
        url: `/zeller-ads/meta/campaign/${campaignId}/adsets${limit ? `?limit=${limit}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsAdsFromAdSet: build.query<unknown, { adSetId: string; limit?: string }>({
      query: ({ adSetId, limit }) => ({
        url: `/zeller-ads/meta/adset/${adSetId}/ads${limit ? `?limit=${limit}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getZellerAdsHierarchy: build.query<MetaHierarchyResponse, { businessUnitId: number }>({
      query: ({ businessUnitId }) => ({
        url: `/zeller-ads/meta/hierarchy?businessUnitId=${businessUnitId}`,
        method: 'GET',
      }),
      transformResponse: (response: MetaHierarchyResponse) => response,
    }),
    disconnectZellerAds: build.mutation<ZellerAdsResponse, void>({
      query: () => ({
        url: '/zeller-ads/meta/disconnect',
        method: 'DELETE',
      }),
      transformResponse: (response: ZellerAdsResponse) => response,
    }),
    getPendingAdAccounts: build.query<PendingAdAccountsResponse, void>({
      query: () => ({
        url: '/meta-ad-accounts/pending',
        method: 'GET',
      }),
      transformResponse: (response: PendingAdAccountsResponse) => response,
    }),
    getAvailableBusinessUnits: build.query<BusinessUnitsResponse, void>({
      query: () => ({
        url: '/meta-ad-accounts/available-business-units',
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnitsResponse) => response,
    }),
    assignAdAccounts: build.mutation<AssignAdAccountsResponse, AssignAdAccountsRequest>({
      query: (body) => ({
        url: '/meta-ad-accounts/assign',
        method: 'POST',
        body,
      }),
      transformResponse: (response: AssignAdAccountsResponse) => response,
    }),
    syncAllMetaData: build.mutation<MetaSyncResponse, void>({
      query: () => ({
        url: '/meta-sync/sync-all',
        method: 'POST',
      }),
      transformResponse: (response: MetaSyncResponse) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetZellerAdsStatusQuery,
  useLazyGetZellerAdsDebugConfigQuery,
  useLazyGetZellerAdsAuthUrlQuery,
  useLazyGetZellerAdsAdAccountsSyncQuery,
  useLazyGetZellerAdsAdAccountsQuery,
  useLazyGetZellerAdsSpecificAdAccountQuery,
  useLazyGetZellerAdsCampaignsQuery,
  useLazyGetZellerAdsAdSetsQuery,
  useLazyGetZellerAdsAdsQuery,
  useLazyGetZellerAdsAdSetsFromCampaignQuery,
  useLazyGetZellerAdsAdsFromAdSetQuery,
  useLazyGetZellerAdsHierarchyQuery,
  useDisconnectZellerAdsMutation,
  useGetPendingAdAccountsQuery,
  useGetAvailableBusinessUnitsQuery,
  useAssignAdAccountsMutation,
  useSyncAllMetaDataMutation,
} = zellerAdsApi;

export type {
  MetaAd,
  MetaAdSet,
  MetaCampaign,
  MetaAdAccount,
  MetaHierarchyResponse,
  ZellerAdsResponse,
  AuthUrlResponse,
  StatusResponse,
  PendingAdAccount,
  PendingAdAccountsResponse,
  BusinessUnit,
  BusinessUnitsResponse,
  AssignAdAccountsRequest,
  AssignAdAccountsResponse,
  MetaSyncResponse,
};

export default zellerAdsApi;
